import { useState, useMemo } from "react"
import { Moon } from "@phosphor-icons/react"
import InfoTooltip from "~/components/reusable/InfoTooltip"
import { addDays, max } from "date-fns"
import { groupBy } from "~/utils/tsUtils"
import { Button } from "~/components/reusable/Button"
import {
  DialogRoot,
  DialogTrigger,
  DialogContentV2,
  DialogHeaderV2,
  DialogBodyV2,
  DialogFooterV2,
  DialogTitle,
  DialogClose,
} from "~/components/reusable/Dialog"
import { api } from "~/utils/api"
import { trackEvent } from "~/hooks/useTrackingService"
import { formatDate, startOfDayUTC, startOfTodayUTC } from "~/utils/datetime"
import { type IVariantSummary } from "~/hooks/useVariantTableData"

const NO_PO_GROUP = "No active PO"

type SnoozeRow = {
  variantId: string
  variantName: string | null
  productName: string
  totalLeadTimeDays: number
  poName: string
  poEad: Date | null
}

function computeSnoozeUntil(row: SnoozeRow, extraDays: number): Date {
  const ead = row.poEad
    ? startOfDayUTC(row.poEad)
    : addDays(startOfTodayUTC(), row.totalLeadTimeDays)
  const reorderPoint = addDays(ead, -row.totalLeadTimeDays)
  const floor = addDays(startOfTodayUTC(), extraDays)
  return max([reorderPoint, floor])
}

type Props = {
  variants: IVariantSummary[]
  delayedPoWarningMap: Map<string, { poName: string; poExpectedArrivalDate: Date }>
  onSuccess?: () => void
  triggerLabel?: string
}

export function SnoozeDialog({
  variants,
  delayedPoWarningMap,
  onSuccess,
  triggerLabel = "Snooze",
}: Props) {
  const [open, setOpen] = useState(false)
  const [extraDays, setExtraDays] = useState(0)

  const utils = api.useUtils()
  const { mutate: snoozeVariants, isPending } = api.variantMetadata.snoozeVariants.useMutation({
    onSuccess: (_data, variables) => {
      trackEvent("Variant Snoozed", { "Number Of Variants Snoozed": variables.length })
      utils.variantMetadata.getSnoozedVariants.invalidate()
      setOpen(false)
      onSuccess?.()
    },
  })

  const rows: SnoozeRow[] = useMemo(
    () =>
      variants.map((v) => {
        const warning = delayedPoWarningMap.get(v.variantId)
        return {
          variantId: v.variantId,
          variantName: v.variantName ?? v.productName,
          productName: v.productName,
          totalLeadTimeDays: v.settings.totalLeadTimeDays,
          poName: warning?.poName ?? NO_PO_GROUP,
          poEad: warning?.poExpectedArrivalDate ?? null,
        }
      }),
    [variants, delayedPoWarningMap],
  )

  const grouped = useMemo(() => {
    const groups = groupBy(rows, (r) => r.poName)
    const poGroups = Object.entries(groups)
      .filter(([key]) => key !== NO_PO_GROUP)
      .sort(([a], [b]) => a.localeCompare(b))
    const noPo = groups[NO_PO_GROUP] ?? []
    return [...poGroups, ...(noPo.length > 0 ? [[NO_PO_GROUP, noPo] as [string, SnoozeRow[]]] : [])]
  }, [rows])

  const handleConfirm = () => {
    snoozeVariants(
      rows.map((row) => ({
        variantId: row.variantId,
        snoozedUntil: computeSnoozeUntil(row, extraDays),
      })),
    )
  }

  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      <DialogTrigger requires="edit" asChild>
        <Button variant="neutral" size="sm">
          <Moon />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContentV2 className="w-[80vw] max-w-[900px]">
        <DialogHeaderV2>
          <DialogTitle>Snooze suggestions</DialogTitle>
        </DialogHeaderV2>
        <DialogBodyV2 className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <span className="text-fg-muted paragraph-sm">Min. days from today</span>
            <input
              type="text"
              inputMode="numeric"
              value={extraDays}
              onChange={(e) => {
                const raw = e.target.value.replace(/\D/g, "")
                const num = raw === "" ? 0 : Math.min(720, parseInt(raw))
                setExtraDays(num)
              }}
              onBlur={(e) => {
                if (e.target.value === "") setExtraDays(0)
              }}
              className="w-16 border border-base rounded-md px-2 py-1 paragraph-sm bg-base text-fg-base text-center"
            />
          </div>

          <div className="border border-base rounded-md overflow-hidden max-h-[50vh] overflow-y-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-fg-base/5 border-b border-base sticky top-0 z-10">
                  <th className="text-left px-3 py-2 paragraph-sm text-fg-muted font-medium">
                    Variant
                  </th>
                  <th className="text-right px-3 py-2 paragraph-sm text-fg-muted font-medium">
                    ETA
                  </th>
                  <th className="text-right px-3 py-2 paragraph-sm text-fg-muted font-medium">
                    <span className="flex items-center justify-end gap-1">
                      Reappears on
                      <InfoTooltip
                        title="ETA minus lead time"
                        body="The last date to place a new order and still receive it before the delayed PO arrives. Add extra days to snooze longer."
                        side="top"
                      />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {grouped.map(([groupName, groupRows]) => (
                  <>
                    <tr
                      key={`group-${groupName}`}
                      className="bg-fg-base/[0.03] border-b border-base"
                    >
                      <td
                        colSpan={3}
                        className="px-3 py-1.5 paragraph-sm text-fg-muted font-medium"
                      >
                        {groupName}
                      </td>
                    </tr>
                    {groupRows.map((row) => (
                      <tr
                        key={row.variantId}
                        className="border-b border-base last:border-b-0 hover:bg-fg-base/5"
                      >
                        <td className="px-3 py-2">
                          <div className="paragraph-sm text-fg-base">{row.variantName}</div>
                          <div className="paragraph-sm text-fg-muted">{row.productName}</div>
                        </td>
                        <td className="px-3 py-2 text-right paragraph-sm text-fg-muted tabular-nums">
                          {row.poEad
                            ? formatDate(row.poEad)
                            : `~${formatDate(addDays(startOfTodayUTC(), row.totalLeadTimeDays))}`}
                        </td>
                        <td className="px-3 py-2 text-right paragraph-sm text-fg-base font-medium tabular-nums">
                          {formatDate(computeSnoozeUntil(row, extraDays))}
                        </td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </DialogBodyV2>
        <DialogFooterV2 className="flex justify-end gap-3">
          <DialogClose asChild>
            <Button variant="neutral" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button size="sm" requires="edit" onClick={handleConfirm} loading={isPending}>
            Snooze {variants.length} variant{variants.length !== 1 ? "s" : ""}
          </Button>
        </DialogFooterV2>
      </DialogContentV2>
    </DialogRoot>
  )
}
